import { Send } from 'lucide-react';
import React, { useRef, useState } from 'react';

import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface ReplyInputProps {
  onSubmit: (content: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
}

export const ReplyInput: React.FC<ReplyInputProps> = ({
  onSubmit,
  placeholder = 'Write a reply...',
  disabled = false,
}) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const canSubmit = content.trim().length > 0 && !isSubmitting && !disabled;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content.trim());
      setContent('');
      textareaRef.current?.focus();
    } catch (_) {
      // Error already handled by onSubmit (toast shown)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="px-5 py-3 border-t border-gray-200 bg-white">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            rows={1}
            className="min-h-[36px] max-h-[120px] resize-none text-[13px] font-medium py-2 px-3 border-gray-200 focus-visible:ring-indigo-500"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              'flex-shrink-0 p-2 rounded-lg transition-colors',
              canSubmit
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            )}
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-gray-500 mt-1 font-medium">
          Press {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to send
        </p>
      </div>
    </div>
  );
};
